/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, {
  DetailedHTMLProps,
  InputHTMLAttributes,
  useEffect,
  useRef,
  useState,
} from "react";
import ReactTooltip from "react-tooltip";
import { useRecoilState, useRecoilValue } from "recoil";
import { NoteCategoriesAPI } from "../../api/NoteCategory";
import { NoteCategoriesAtom } from "../../atoms/NotesAtom";
import { UserAtom } from "../../atoms/UserAtom";
import useClickOutside from "../../hook/useClickOutside";
import { Ellipsis } from "../../styles/general";
import { IOption } from "../../types/CommonTypes";
import { INoteType } from "../../types/NoteCategory";
import { getScrollParent } from "../../utils/DOMHelper";
import IconBerserkerMail from "../Icon";
import StyledButton from "../StyledButton";
import { AutoCompleteSelectItem } from "../TagSelect/AutoCompleteItem";

const Container = styled.div`
  width: 100%;
  height: 40px;
  display: flex;
  position: relative;
`;

const InputContainer = styled.div`
  width: 100%;

  .__react_component_tooltip {
    padding: 8px !important;
    background: #c41700 !important;
    &::after {
      left: 5% !important;
    }
  }
`;

const StyledIcon = styled.div`
  position: absolute;
  left: 10px;
  top: 10px;
`;

interface InputProps {
  active: boolean;
}

const Input = styled.input<InputProps>`
  width: 100%;
  height: 40px;
  border: 1px solid #c41700;
  box-shadow: 0px 0px 6px #c417004d;

  border-radius: 5px;
  outline: none;
  font-size: 14px;
  padding: 5px;
  color: #383838;
  text-indent: 25px;
  background-color: #ffffff;

  ${(props) =>
    props.active &&
    css`
      box-shadow: 0px 0px 6px #fb972e4d;
      border: 1px solid #fb972e;
    `}

  &::placeholder {
    font-size: 14px;
    letter-spacing: 0.16px;
    color: #979797;
  }
`;

const AutoCompleteDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  border-radius: 5px;
  background-color: #ffffff;
`;

interface DropViewProps {
  position: { top: number; left: number };
  width: number;
}

const StyledAutoCompleteList = styled.div`
  position: fixed;
  background-color: #fff;
  z-index: 15;
  max-height: 260px;
  flex: 1;
  overflow: auto;
  border-radius: 5px;
  box-shadow: 0px 3px 6px #0000001a;
  border: 1px solid #d4d4d4;
  border-bottom: none;
  border-top: none;
  right: 0;
  top: ${(props: DropViewProps) => props.position.top}px;
  left: ${(props: DropViewProps) => props.position.left}px;
  width: ${(props: DropViewProps) => props.width}px;
  cursor: default;

  &:hover {
    background-color: #fb972e;
    color: #fff;
  }
`;

const GroupContainer = styled.div`
  display: flex;
  align-items: center;
  background-color: #fff;
`;

const GroupName = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
  padding-left: 15px;
  color: #000;
  font-weight: 600;
  background-color: #fff;
`;

export const SystemTag = styled.div`
  font-size: 11px;
  background-color: #eceeef;
  color: #9d9e9e;
  padding: 4px;
  margin-left: 10px;
`;

const TooltipText = styled.span`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0px;
  color: #ffffff;
`;

const NoResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #fff;
  color: #000;
  padding: 20px 15px 30px 15px;
  text-align: center;
`;
const IconContainer = styled.div``;

const TitleNoResults = styled.div`
  margin-top: 10px;
  font-weight: bold;
  margin-bottom: 12px;
`;
const Content = styled.div`
  color: #666666;
  margin-bottom: 5px;
`;

const CreateTagButton = styled(StyledButton)`
  margin-top: 12px;
`;

export interface INoteCategoryOption {
  type: INoteType;
  id: string;
  name: string;
}

type IStyledAutocompleteSelectProps = DetailedHTMLProps<
  InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> & {
  placeholder?: string;
  suggestions: INoteCategoryOption[] | undefined;
  selectedValue: string;
  onEnter: (options: INoteCategoryOption) => void;
  onValueChange?: (value: string) => void;
  onValueSelect: (options: INoteCategoryOption) => void;
  onCancel: () => void;
  optionIsSelected: boolean;
  isFilter?: boolean;
};

interface IItem {
  groupName?: string;
  name: string;
  icon?: string;
  suffix?: string;
  index: number;
  isActive: boolean;
  type: INoteType;
}

const StyledClearIcon = styled.div<{ isCreatingTag: boolean }>`
  position: absolute;
  cursor: pointer;
  right: ${(props) => (props.isCreatingTag ? "80px" : "14px")};
  top: 12px;
`;

export default function AutoCompleteNoteCategory(
  props: IStyledAutocompleteSelectProps
): JSX.Element {
  const {
    suggestions,
    selectedValue,
    onValueSelect,
    onValueChange,
    placeholder,
    optionIsSelected,
    isFilter,
  } = props;
  const [currentFocus, setCurrentFocus] = useState(0);
  const [filteredSuggestions, setFilteredSuggestions] = useState<IItem[]>();
  const user = useRecoilValue(UserAtom);

  /**
   * Select functionality
   */
  const [openDropView, setOpenDropView] = useState<boolean>(false);

  /**
   * Refs to keep track
   */
  const wrapperRef = useClickOutside(() => setOpenDropView(false));
  const selectRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const suggestionRefMap = useRef<{
    [suggestionValue: string]: HTMLDivElement | null;
  }>({});
  const [categories, setCategories] = useRecoilState(NoteCategoriesAtom);

  const [dropviewPosition, setDropViewPosition] = useState<{
    top: number;
    left: number;
  }>({
    top: 0,
    left: 0,
  });
  const [dropViewWidth, setDropViewWidth] = useState<number>(0);
  /**
   * Update DropView position to match current Input location on screen.
   *
   * This function moves the DropView below the Input position.
   */
  function updateDropViewPosition() {
    if (!wrapperRef.current) return;

    const elementBoundingRect = (
      wrapperRef.current as HTMLDivElement
    ).getBoundingClientRect();

    setDropViewPosition({
      top: elementBoundingRect.top + elementBoundingRect.height,
      left: elementBoundingRect.left,
    });

    setDropViewWidth(elementBoundingRect.width);
  }

  /**
   * useEffect that closes the DropView when a scroll is detected, much like
   * the native <select />
   */
  useEffect(() => {
    if (!openDropView) {
      setCurrentFocus(0);
      return () => {};
    }

    function closeDropView() {
      setOpenDropView(false);
    }

    const scrollParent = getScrollParent(selectRef.current, selectRef.current);

    if (!scrollParent) return () => {};

    scrollParent.addEventListener("scroll", closeDropView);

    return () => {
      scrollParent.removeEventListener("scroll", closeDropView);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrapperRef, openDropView]);

  useEffect(() => {
    if (suggestions) {
      const suggestionsArray = suggestions.map((suggestion, index) => ({
        ...suggestion,
        type: suggestion.type,
        index,
        isActive: false,
      }));

      setFilteredSuggestions(suggestionsArray);
    }
  }, [suggestions]);

  /**
   * On window resize, calculate position
   */
  useEffect(() => {
    window.addEventListener("resize", updateDropViewPosition);
    return () => window.removeEventListener("resize", updateDropViewPosition);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrapperRef]);

  useEffect(() => {
    setFilteredSuggestions((prevSuggestionList) => {
      if (prevSuggestionList) {
        return prevSuggestionList.map((suggestionItem, index) => {
          if (index === currentFocus) {
            return {
              ...suggestionItem,
              isActive: true,
            };
          }
          return {
            ...suggestionItem,
            isActive: false,
          };
        });
      }
      return undefined;
    });
  }, [currentFocus]);

  function typedValidValue(): INoteCategoryOption | undefined {
    if (suggestions && selectedValue) {
      const tag = suggestions.find((suggestion) => {
        return suggestion.name === selectedValue;
      });
      return tag;
    }
    return undefined;
  }

  function getContainerWidth() {
    if (!containerRef.current) return 0;

    const elementBoundingRect = (
      containerRef.current as HTMLDivElement
    ).getBoundingClientRect();

    return (elementBoundingRect.width - 330) / 2;
  }

  function handleClick() {
    if (suggestions) {
      const suggestionsArray = suggestions
        .map((suggestion, index) => ({
          ...suggestion,
          index,
          isActive: false,
        }))
        .filter((suggestion) => {
          return suggestion.name
            .toLocaleLowerCase()
            .includes(selectedValue.toLocaleLowerCase());
        });

      setFilteredSuggestions(suggestionsArray);
    }
    updateDropViewPosition();
    setOpenDropView(true);
  }

  function handleLoseFocus() {
    const category = typedValidValue();
    // The value he typed is already a value in the suggestion list
    if (category) {
      setOpenDropView(false);
      onValueSelect({
        id: `${category.id}`,
        name: category.name,
        type: category.type,
      });
    }
  }

  function handleItemClick(item: IItem) {
    if (suggestions) {
      const category = suggestions.find(
        (suggestion) => suggestion.name === item.name
      ) as INoteCategoryOption;

      setOpenDropView(false);

      onValueSelect({
        id: `${category.id}`,
        name: category.name,
        type: category.type,
      });
    }
  }

  function handleInputKeyDown(e: React.KeyboardEvent) {
    if (suggestions && filteredSuggestions) {
      const lastItem = filteredSuggestions.length - 1;
      if (e.key === "ArrowDown") {
        e.preventDefault();

        let currentIndex = 0;

        if (currentFocus === lastItem) {
          setCurrentFocus(0);
        } else {
          setCurrentFocus(currentFocus + 1);
          currentIndex = currentFocus + 1;
        }

        let currentSuggestionActive = filteredSuggestions[currentIndex];

        suggestionRefMap.current[currentSuggestionActive.name]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();

        let currentIndex = lastItem;
        if (currentFocus === 0) {
          setCurrentFocus(lastItem);
        } else {
          setCurrentFocus(currentFocus - 1);
          currentIndex = currentFocus - 1;
        }

        let currentSuggestionActive = filteredSuggestions[currentIndex];

        suggestionRefMap.current[currentSuggestionActive.name]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (currentFocus > -1) {
          if (filteredSuggestions[currentFocus]) {
            const category = suggestions[currentFocus];
            setOpenDropView(false);

            onValueSelect({
              id: `${category.id}`,
              name: category.name,
              type: category.type,
            });
          }
        } else {
          const category = typedValidValue();
          if (category) {
            setOpenDropView(false);

            onValueSelect({
              id: `${category.id}`,
              name: category.name,
              type: category.type,
            });
          }
        }
      }
    }
  }

  function clearInput() {
    if (!onValueChange) return;
    onValueChange("");
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    setCurrentFocus(-1);

    if (onValueChange) onValueChange(value);
    if (suggestions) {
      const suggestionsArray = suggestions
        .map((suggestion, index) => ({
          ...suggestion,
          index,
          isActive: false,
        }))
        .filter((suggestion) => {
          return suggestion.name
            .toLocaleLowerCase()
            .includes(value.toLocaleLowerCase());
        });

      setFilteredSuggestions(suggestionsArray);
    }
  }

  function returnTypeArray() {
    if (!filteredSuggestions) return;

    if (!isFilter) {
      return filteredSuggestions
        .filter((c) => {
          if (c.type === INoteType.USER) return true;
          return false;
        })
        .map((suggestionItem) => {
          return (
            <div
              ref={(r) => (suggestionRefMap.current[suggestionItem.name] = r)}
            >
              <AutoCompleteSelectItem
                key={suggestionItem.index + suggestionItem.name}
                onClick={() => {
                  handleItemClick(suggestionItem);
                }}
                name={suggestionItem.name}
                isActive={suggestionItem.isActive}
                customStyle={{
                  paddingLeft: "30px",
                }}
                alreadyWritten={selectedValue}
              />
            </div>
          );
        });
    } else {
      return [INoteType.SYSTEM, INoteType.USER].map((e) => {
        return (
          <div
            css={css`
              width: 100%;
            `}
          >
            <GroupContainer>
              <GroupName>
                {e === INoteType.SYSTEM ? "System" : "User"}
              </GroupName>
            </GroupContainer>

            {filteredSuggestions
              .filter((c) => {
                if (c.type === e) return true;
                return false;
              })
              .map((suggestionItem) => {
                return (
                  <div
                    ref={(r) =>
                      (suggestionRefMap.current[suggestionItem.name] = r)
                    }
                  >
                    <AutoCompleteSelectItem
                      key={suggestionItem.index + suggestionItem.name}
                      onClick={() => {
                        handleItemClick(suggestionItem);
                      }}
                      name={suggestionItem.name}
                      isActive={suggestionItem.isActive}
                      customStyle={{
                        paddingLeft: "30px",
                      }}
                      alreadyWritten={selectedValue}
                    />
                  </div>
                );
              })}
          </div>
        );
      });
    }
  }

  return (
    <Container ref={containerRef}>
      <AutoCompleteDiv ref={selectRef}>
        <InputContainer data-tip data-for="error" ref={wrapperRef}>
          {!optionIsSelected && (
            <ReactTooltip
              id="error"
              place="top"
              type="dark"
              effect="solid"
              offset={{
                left: getContainerWidth() > 0 ? getContainerWidth() : 0,
              }}
              arrowColor={"#c41700"}
            >
              <TooltipText>
                {"You must choose a category to save your new note"}
              </TooltipText>
            </ReactTooltip>
          )}

          <Input
            onClick={handleClick}
            onBlur={handleLoseFocus}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            value={selectedValue}
            type="text"
            placeholder={placeholder}
            autoComplete="off"
            key={`dropViewWidth ${dropViewWidth} - ${dropviewPosition.left} - ${dropviewPosition.top}`}
            active={optionIsSelected}
          />
          {openDropView && (
            <StyledAutoCompleteList
              position={dropviewPosition}
              width={dropViewWidth}
            >
              {filteredSuggestions && filteredSuggestions.length > 0 ? (
                returnTypeArray()
              ) : (
                <NoResultsContainer>
                  <IconContainer>
                    <IconBerserkerMail name="noResultsFound" />
                    <TitleNoResults>No Results Found</TitleNoResults>
                  </IconContainer>
                  <Content
                    css={css`
                      max-width: 90%;
                      ${Ellipsis}
                    `}
                  >{`No categories found for "${selectedValue}"`}</Content>
                  <Content>
                    Do you want to create this as new note category?
                  </Content>
                  <CreateTagButton
                    disable={selectedValue === ""}
                    onClick={async () => {
                      if (!user) return;

                      const foundSameCat = categories.find(
                        (c) =>
                          c.name.toLowerCase().replace(/\s\s+/g, " ").trim() ===
                          selectedValue
                            .toLowerCase()
                            .replace(/\s\s+/g, " ")
                            .trim()
                      );

                      if (foundSameCat) return;

                      const category = await NoteCategoriesAPI.create({
                        name: selectedValue.replace(/\s\s+/g, " ").trim(),
                        type: INoteType.USER,
                      });
                      if (category) {
                        setOpenDropView(false);

                        setCategories([
                          ...categories,
                          {
                            _id: category._id,
                            name: selectedValue,
                            type: INoteType.USER,
                            userUid: "1",
                          },
                        ]);

                        onValueSelect({
                          id: category._id?.toString() || "",
                          name: selectedValue,
                          type: INoteType.USER,
                        });
                      }
                    }}
                  >
                    Create as new category
                  </CreateTagButton>
                </NoResultsContainer>
              )}
            </StyledAutoCompleteList>
          )}
        </InputContainer>
      </AutoCompleteDiv>
      <StyledIcon>
        <IconBerserkerMail name="search" height={16} width={16} />
      </StyledIcon>
      {selectedValue.length > 0 && (
        <StyledClearIcon
          isCreatingTag={false}
          onClick={(e) => {
            e.stopPropagation();
            clearInput();
          }}
        >
          <IconBerserkerMail
            name="close-modal"
            width={12}
            height={12}
            color={"#bbb"}
          />
        </StyledClearIcon>
      )}
    </Container>
  );
}
